import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tokensApi,
  usageApi,
  billingApi,
  couponsApi,
  proxyApi,
  paystackApi,
  redemptionApi,
  fxApi,
  notificationsApi,
  getErrorMessage,
} from '@/lib/api';
import {
  generateMockUsageStats,
  generateMockBillingInfo,
  generateMockInvoices,
  generateMockCoupons,
} from '@/lib/mockAuth';
import { PLANS } from '@/lib/config';
import type {
  UsageStats,
  BillingInfo,
  Invoice,
  Coupon,
  CreateTokenRequest,
  CreateCouponRequest,
  ProxyRequest,
  Currency,
  PaystackPlan,
} from '@/types';

const isDevelopment = import.meta.env.MODE === 'development';

// Query Keys
export const queryKeys = {
  tokens: ['tokens'] as const,
  usage: (period?: string) => ['usage', period] as const,
  tokenUsage: (tokenId: string, period?: string) => ['usage', 'tokens', tokenId, period] as const,
  billing: ['billing'] as const,
  invoices: ['invoices'] as const,
  coupons: ['coupons'] as const,
  coupon: (id: string) => ['coupons', id] as const,
  plans: (currency?: Currency) => ['plans', currency] as const,
  subscription: ['subscription'] as const,
  usageSummary: (params?: string) => ['usage-summary', params] as const,
  accountBalance: ['account-balance'] as const,
  paymentHistory: (page: number, limit: number) => ['payment-history', page, limit] as const,
  myCoupons: ['my-coupons'] as const,
  ngnUsdRate: ['ngn-usd-rate'] as const,
  notifications: (before?: number) => ['notifications', before] as const,
  unreadNotificationCount: ['unread-notification-count'] as const,
};

// Tokens Hooks
export const useTokens = () => {
  return useQuery({
    queryKey: queryKeys.tokens,
    queryFn: async () => {
      const response = await tokensApi.list();
      return response.data;
    },
  });
};

export const useCreateToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTokenRequest) => {
      const response = await tokensApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tokens });
    },
  });
};

export const useRevokeToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await tokensApi.revoke(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tokens });
    },
  });
};

export const useRotateToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await tokensApi.rotate(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tokens });
    },
  });
};

// Usage Hooks
export const useUsageStats = (period?: string) => {
  return useQuery({
    queryKey: queryKeys.usage(period),
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 800));
        return generateMockUsageStats();
      }
      const response = await usageApi.getStats(period);
      return response.data;
    },
  });
};

export const useTokenUsage = (tokenId: string, period?: string) => {
  return useQuery({
    queryKey: queryKeys.tokenUsage(tokenId, period),
    queryFn: async () => {
      const response = await usageApi.getTokenUsage(tokenId, period);
      return response.data;
    },
    enabled: !!tokenId,
  });
};

export const useUsageSummary = (params?: { start?: number; end?: number; granularity?: 'hour' | 'day' | 'month'; environment?: 'sandbox' | 'production' }) => {
  return useQuery({
    queryKey: queryKeys.usageSummary(JSON.stringify(params ?? {})),
    queryFn: async () => {
      const response = await usageApi.getSummary(params);
      return response.data;
    },
  });
};

// Billing Hooks
export const useBillingInfo = () => {
  return useQuery({
    queryKey: queryKeys.billing,
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 600));
        return generateMockBillingInfo();
      }
      const response = await billingApi.getInfo();
      return response.data;
    },
  });
};

export const useAccountBalance = () => {
  return useQuery({
    queryKey: queryKeys.accountBalance,
    queryFn: async () => {
      const response = await billingApi.getBalance();
      return response.data;
    },
  });
};

export const usePurchaseCredits = () => {
  return useMutation({
    mutationFn: async (data: { amount: number; credits: number }) => {
      const response = await billingApi.purchaseCredits(data);
      return response.data;
    },
  });
};

export const usePaymentHistory = (page: number, limit: number = 20) => {
  return useQuery({
    queryKey: queryKeys.paymentHistory(page, limit),
    queryFn: async () => {
      const response = await billingApi.getHistory(page, limit);
      return response.data;
    },
  });
};

export const useInvoices = () => {
  return useQuery({
    queryKey: queryKeys.invoices,
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 400));
        return generateMockInvoices();
      }
      const response = await billingApi.getInvoices();
      return response.data;
    },
  });
};

export const useDownloadInvoice = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await billingApi.downloadInvoice(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return response.data;
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await billingApi.cancelSubscription();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing });
    },
  });
};

// Paystack / Plan Hooks
export const usePlans = (currency?: Currency) => {
  return useQuery<PaystackPlan[]>({
    queryKey: queryKeys.plans(currency),
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 300));
        return PLANS;
      }
      const response = await paystackApi.getPlans(currency);
      return response.data;
    },
  });
};

export const useInitializePayment = () => {
  return useMutation({
    mutationFn: async (data: {
      email: string;
      amount: number;
      currency: Currency;
      plan?: string;
      reference?: string;
      callbackUrl?: string;
    }) => {
      const response = await paystackApi.initializeTransaction(data);
      return response.data;
    },
  });
};

export const useVerifyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reference: string) => {
      const response = await paystackApi.verifyTransaction(reference);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing });
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription });
    },
  });
};

export const useSubscription = () => {
  return useQuery({
    queryKey: queryKeys.subscription,
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 400));
        return generateMockBillingInfo();
      }
      const response = await billingApi.getInfo();
      return response.data;
    },
  });
};

export const useSwitchCurrency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (currency: Currency) => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 300));
        return { currency };
      }
      const response = await paystackApi.switchCurrency(currency);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans() });
    },
  });
};

// Coupons Hooks
export const useCoupons = () => {
  return useQuery({
    queryKey: queryKeys.coupons,
    queryFn: async () => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 400));
        return generateMockCoupons();
      }
      const response = await couponsApi.getAll();
      return response.data;
    },
  });
};

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCouponRequest) => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const newCoupon: Coupon = {
          id: `cpn_${Date.now()}`,
          ...data,
          isActive: true,
          usageCount: 0,
        };
        return newCoupon;
      }
      const response = await couponsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons });
    },
  });
};

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return id;
      }
      await couponsApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons });
    },
  });
};

export const useToggleCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, activate }: { id: string; activate: boolean }) => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 300));
        return { id, isActive: activate } as Coupon;
      }
      await couponsApi.setActive(id, activate);
      return { id, isActive: activate };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons });
    },
  });
};

// Proxy Hook
export const useProxyRequest = () => {
  return useMutation({
    mutationFn: async (data: ProxyRequest) => {
      if (isDevelopment) {
        await new Promise(resolve => setTimeout(resolve, 800));
        return {
          status: 200,
          statusText: 'OK',
          headers: { 'content-type': 'application/json' },
          data: { message: 'Mock response', timestamp: new Date().toISOString() },
          duration: Math.floor(Math.random() * 500) + 50,
        };
      }
      const response = await proxyApi.makeRequest(data);
      return response.data;
    },
  });
};

// Real coupon redemption hooks (distinct from the admin-shaped couponsApi hooks above)
export const useRedeemCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await redemptionApi.redeem(code);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myCoupons });
    },
  });
};

export const useMyCoupons = () => {
  return useQuery({
    queryKey: queryKeys.myCoupons,
    queryFn: async () => {
      const response = await redemptionApi.getMy();
      return response.data;
    },
  });
};

export const useNgnUsdRate = () => {
  return useQuery({
    queryKey: queryKeys.ngnUsdRate,
    queryFn: fxApi.getNgnPerUsd,
    staleTime: 60 * 60 * 1000, // 1 hour — a live rate, not a per-render fetch
    retry: false,
  });
};

export const useNotifications = (before?: number) => {
  return useQuery({
    queryKey: queryKeys.notifications(before),
    queryFn: async () => {
      const response = await notificationsApi.list(before);
      return response.data;
    },
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: queryKeys.unreadNotificationCount,
    queryFn: async () => {
      const response = await notificationsApi.getUnreadCount();
      return response.data;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await notificationsApi.markRead(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await notificationsApi.markAllRead();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
    },
  });
};

// Error handling hook
export const useApiError = () => {
  return {
    getErrorMessage,
    handleError: (error: unknown) => {
      const message = getErrorMessage(error);
      console.error('API Error:', error);
      return message;
    },
  };
};
